/**
 * Per-card mute toggle for the customer reels strip.
 *
 * Cards autoplay muted and stay muted — the only way browsers permit autoplay;
 * unmuting is an explicit user action. Unmuting one card mutes the others so two
 * clips never talk over each other.
 */
function reelCards(root) {
  return Array.from(root.querySelectorAll('.customer-reels__card'));
}

/* Only play what is on screen — a strip of autoplaying clips is otherwise a
   needless drain on battery and bandwidth. */
if ('IntersectionObserver' in window) {
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = entry.target;
        if (entry.isIntersecting) {
          // readyState 0 means no frame was ever decoded, so it would paint blank.
          if (video.readyState === 0) video.load?.();
          video.play?.().catch(() => {});
        }
        else video.pause?.();
      }
    },
    { threshold: 0.25, rootMargin: '200px 0px' }
  );

  const attach = () => {
    for (const video of document.querySelectorAll('.customer-reels__media')) {
      if (video.tagName === 'VIDEO') observer.observe(video);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
}


/* Scroll-forward arrow + fade state. The fade and arrow hide once the track is
   scrolled to the end so they don't imply more content than exists. */
function updateEndState(viewport) {
  const track = viewport.querySelector('[data-reels-track]');
  if (!track) return;
  const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
  const atStart = track.scrollLeft <= 2;
  viewport.setAttribute('data-at-end', String(atEnd));
  viewport.setAttribute('data-at-start', String(atStart));
}

/** One "page" of scrolling: two cards plus their gaps. */
function scrollStep(track) {
  const card = track.querySelector('.customer-reels__card');
  const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
  const one = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.4;
  return one * 2;
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-reels-next], [data-reels-prev]');
  if (!button) return;

  const viewport = button.closest('.customer-reels__viewport');
  const track = viewport?.querySelector('[data-reels-track]');
  if (!track) return;

  const back = button.hasAttribute('data-reels-prev');
  track.scrollBy({ left: back ? -scrollStep(track) : scrollStep(track), behavior: 'smooth' });
});

/*
 * updateEndState reads scrollLeft/clientWidth/scrollWidth and then writes two
 * attributes. Called straight from a scroll listener that is one read/write cycle
 * per event, which is a forced synchronous reflow each time - Lighthouse attributed
 * 37-68ms of reflow to this file. Coalescing to one run per animation frame keeps
 * the reads batched, and the pending frame is tracked per viewport so a page with
 * more than one strip does not drop updates for the others.
 */
const pendingFrames = new WeakMap();

function scheduleEndState(viewport) {
  if (pendingFrames.has(viewport)) return;
  pendingFrames.set(
    viewport,
    requestAnimationFrame(() => {
      pendingFrames.delete(viewport);
      updateEndState(viewport);
    })
  );
}

function initViewports() {
  for (const viewport of document.querySelectorAll('.customer-reels__viewport')) {
    const track = viewport.querySelector('[data-reels-track]');
    if (!track) continue;

    // The first measurement only decides whether a fade and arrow are shown, so it
    // does not need to happen during the load window where it costs a reflow.
    const idle = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;
    idle(() => updateEndState(viewport));

    track.addEventListener('scroll', () => scheduleEndState(viewport), { passive: true });
    window.addEventListener('resize', () => scheduleEndState(viewport), { passive: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initViewports);
} else {
  initViewports();
}
